import { useState, useEffect } from 'react'
import { doc, getDoc, setDoc } from 'firebase/firestore'
import { db } from '../../lib/firebase'
import './AdminEditor.css'

const DEFAULT_SETTINGS = {
  siteName: 'Pel -o Ingolé',
  siteTagline: 'A D&D Campaign',
  primaryColor: '#6E2EBF',
  primaryHover: '#9B5FFF',
  primaryMuted: '#4B1F7A',
  accentColor: '#7A1E2E',
  bgMain: '#141018',
  bgSurface: '#1E1A22',
  bgElevated: '#0B0B0F',
  textPrimary: '#C9C6CF',
  textSecondary: '#ACA9B5',
  textHeading: '#E6E4EA',
}

export default function SiteSettingsEditor() {
  const [settings, setSettings] = useState(DEFAULT_SETTINGS)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(null)

  useEffect(() => {
    loadSettings()
  }, [])

  const loadSettings = async () => {
    try {
      setLoading(true)
      const docRef = doc(db, 'config', 'siteSettings')
      const snapshot = await getDoc(docRef)

      if (snapshot.exists()) {
        setSettings({ ...DEFAULT_SETTINGS, ...snapshot.data() })
      } else {
        setSettings(DEFAULT_SETTINGS)
      }
    } catch (err) {
      setError('Failed to load settings: ' + err.message)
    } finally {
      setLoading(false)
    }
  }

  const handleChange = (field, value) => {
    setSettings(prev => ({ ...prev, [field]: value }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setSaving(true)
    setError(null)
    setSuccess(null)

    try {
      const docRef = doc(db, 'config', 'siteSettings')
      await setDoc(docRef, settings)
      setSuccess('Settings saved successfully')
      setTimeout(() => setSuccess(null), 3000)
    } catch (err) {
      setError('Failed to save settings: ' + err.message)
    } finally {
      setSaving(false)
    }
  }

  const handleReset = () => {
    if (window.confirm('Reset all settings to default values?')) {
      setSettings(DEFAULT_SETTINGS)
    }
  }

  if (loading) {
    return (
      <div className="container">
        <div className="loading-spinner" />
      </div>
    )
  }

  return (
    <div className="container">
      <div className="editor-header">
        <h1>Site Settings</h1>
      </div>

      <p className="text-secondary mb-lg">
        Customize the site branding and theme colors. Changes apply immediately to all users.
      </p>

      {error && <div className="error-banner mb-lg">{error}</div>}
      {success && <div className="success-banner mb-lg">{success}</div>}

      <form onSubmit={handleSubmit} className="editor-form">
        {/* Branding Section */}
        <div className="form-section">
          <h3>Branding</h3>

          <div className="form-group">
            <label htmlFor="siteName">Site Name</label>
            <input
              type="text"
              id="siteName"
              value={settings.siteName}
              onChange={(e) => handleChange('siteName', e.target.value)}
              placeholder="My Campaign"
              disabled={saving}
            />
          </div>

          <div className="form-group">
            <label htmlFor="siteTagline">Tagline</label>
            <input
              type="text"
              id="siteTagline"
              value={settings.siteTagline}
              onChange={(e) => handleChange('siteTagline', e.target.value)}
              placeholder="A D&D Campaign"
              disabled={saving}
            />
          </div>
        </div>

        {/* Primary Colors Section */}
        <div className="form-section">
          <h3>Primary Theme Colors</h3>
          <p className="text-secondary mb-md">
            The main accent color used for buttons, links, and highlights.
          </p>

          <div className="color-grid">
            <ColorInput
              label="Primary Color"
              value={settings.primaryColor}
              onChange={(v) => handleChange('primaryColor', v)}
              disabled={saving}
            />
            <ColorInput
              label="Primary Hover"
              value={settings.primaryHover}
              onChange={(v) => handleChange('primaryHover', v)}
              disabled={saving}
            />
            <ColorInput
              label="Primary Muted"
              value={settings.primaryMuted}
              onChange={(v) => handleChange('primaryMuted', v)}
              disabled={saving}
            />
            <ColorInput
              label="Accent/Danger Color"
              value={settings.accentColor}
              onChange={(v) => handleChange('accentColor', v)}
              disabled={saving}
            />
          </div>
        </div>

        {/* Background Colors Section */}
        <div className="form-section">
          <h3>Background Colors</h3>
          <p className="text-secondary mb-md">
            Background colors for different surface levels.
          </p>

          <div className="color-grid">
            <ColorInput
              label="Main Background"
              value={settings.bgMain}
              onChange={(v) => handleChange('bgMain', v)}
              disabled={saving}
            />
            <ColorInput
              label="Surface Background"
              value={settings.bgSurface}
              onChange={(v) => handleChange('bgSurface', v)}
              disabled={saving}
            />
            <ColorInput
              label="Elevated Background"
              value={settings.bgElevated}
              onChange={(v) => handleChange('bgElevated', v)}
              disabled={saving}
            />
          </div>
        </div>

        {/* Text Colors Section */}
        <div className="form-section">
          <h3>Text Colors</h3>
          <p className="text-secondary mb-md">
            Colors for text content at different emphasis levels.
          </p>

          <div className="color-grid">
            <ColorInput
              label="Primary Text"
              value={settings.textPrimary}
              onChange={(v) => handleChange('textPrimary', v)}
              disabled={saving}
            />
            <ColorInput
              label="Secondary Text"
              value={settings.textSecondary}
              onChange={(v) => handleChange('textSecondary', v)}
              disabled={saving}
            />
            <ColorInput
              label="Heading Text"
              value={settings.textHeading}
              onChange={(v) => handleChange('textHeading', v)}
              disabled={saving}
            />
          </div>
        </div>

        {/* Preview Section */}
        <div className="form-section">
          <h3>Preview</h3>
          <div
            className="theme-preview"
            style={{
              '--preview-primary': settings.primaryColor,
              '--preview-primary-hover': settings.primaryHover,
              '--preview-accent': settings.accentColor,
              '--preview-bg-main': settings.bgMain,
              '--preview-bg-surface': settings.bgSurface,
              '--preview-text-primary': settings.textPrimary,
              '--preview-text-secondary': settings.textSecondary,
              '--preview-text-heading': settings.textHeading,
            }}
          >
            <div className="preview-card">
              <h4 style={{ color: 'var(--preview-text-heading)' }}>
                {settings.siteName}
              </h4>
              <p style={{ color: 'var(--preview-text-secondary)' }}>
                {settings.siteTagline}
              </p>
              <p style={{ color: 'var(--preview-text-primary)' }}>
                Sample body text showing how content will appear.
              </p>
              <div className="preview-buttons">
                <button
                  type="button"
                  className="preview-btn-primary"
                  style={{
                    background: 'var(--preview-primary)',
                    color: 'var(--preview-text-heading)',
                  }}
                >
                  Primary Button
                </button>
                <button
                  type="button"
                  className="preview-btn-accent"
                  style={{
                    background: 'var(--preview-accent)',
                    color: 'var(--preview-text-heading)',
                  }}
                >
                  Accent Button
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="form-actions">
          <button
            type="button"
            className="btn btn-ghost"
            onClick={handleReset}
            disabled={saving}
          >
            Reset to Defaults
          </button>
          <button type="submit" className="btn btn-primary" disabled={saving}>
            {saving ? 'Saving...' : 'Save Settings'}
          </button>
        </div>
      </form>
    </div>
  )
}

function ColorInput({ label, value, onChange, disabled }) {
  return (
    <div className="color-input-group">
      <label>{label}</label>
      <div className="color-input-wrapper">
        <input
          type="color"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
          className="color-picker"
        />
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
          className="color-text"
          placeholder="#000000"
          pattern="^#[0-9A-Fa-f]{6}$"
        />
      </div>
    </div>
  )
}
